import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { userStore } from '../../store';
import { hitApi } from '../../database/services/ApiHelper';
import { API } from '../../database/apis';
import {
  ArrowLeft,
  BookOpen,
  Heart,
  HelpCircle,
  Leaf,
  User as UserIcon,
  Rocket,
} from 'lucide-react';

// BrowseAll — "View all" destination from the Discover landing's
// "Browse Popular" section. Shows the same six curated chips plus
// every other category surfaced by the backend search?mode=genres
// endpoint (which counts books per category and returns sample covers).

type GenreResult = {
  name: string;
  bookCount: number;
  coverSamples: string[];
};

const BRAND = '#5B47E0';
const TEXT_PRIMARY = '#0F172A';
const TEXT_MUTED = '#64748B';
const SURFACE = '#F1F5F9';
const BRAND_LIGHT = '#EDEAFC';

// The same six curated categories pinned to the top of the page so the
// visual matches the Discover landing's Browse Popular tile.
const CURATED: { label: string; icon: any; bg: string; fg: string }[] = [
  { label: 'Fiction',   icon: BookOpen,   bg: '#EEF2FF', fg: '#4F46E5' },
  { label: 'Romance',   icon: Heart,      bg: '#FCE7F3', fg: '#DB2777' },
  { label: 'Mystery',   icon: HelpCircle, bg: '#DBEAFE', fg: '#2563EB' },
  { label: 'Self-Help', icon: Leaf,       bg: '#D1FAE5', fg: '#059669' },
  { label: 'Biography', icon: UserIcon,   bg: '#FED7AA', fg: '#EA580C' },
  { label: 'Sci-Fi',    icon: Rocket,     bg: '#EDE9FE', fg: '#7C3AED' },
];

const BrowseAllScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const [genres, setGenres] = useState<GenreResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void fetchGenres();
  }, []);

  async function fetchGenres() {
    setLoading(true);
    try {
      const token = userStore.authToken || userStore.deviceToken;
      const res: any = await hitApi({
        endpoint: `${API.SEARCH}?q=&mode=genres&limit=50`,
        method: 'GET',
        token,
      });
      setGenres(Array.isArray(res?.genres) ? res.genres : []);
    } catch (e) {
      // hitApi throws { status, message } objects (not Error). Swallow
      // locally so the dev overlay doesn't show "[object Object]".
      console.log('[BrowseAll] fetch failed', e);
      setGenres([]);
    } finally {
      setLoading(false);
    }
  }

  function openCategory(label: string) {
    navigate(`/app/search?category=${encodeURIComponent(label)}`);
  }

  // Filter out genres that are already in the curated list so the
  // "All categories" section doesn't duplicate Fiction etc.
  const curatedNames = new Set(CURATED.map((c) => c.label.toLowerCase()));
  const otherGenres = genres.filter((g) => !curatedNames.has(g.name.toLowerCase()));

  return (
    <div className="h-full flex flex-col" style={{ background: '#FFFFFF' }}>
      {/* Top bar */}
      <div className="safe-area-top" style={{ background: '#FFFFFF' }}>
        <div className="px-5 pt-4 pb-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{ background: SURFACE }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: TEXT_PRIMARY }} />
          </button>
          <h1
            className="text-[22px] font-bold tracking-tight"
            style={{ color: TEXT_PRIMARY }}
          >
            Browse Categories
          </h1>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto pb-6">
        {/* Curated section */}
        <section className="px-5 pt-1">
          <p className="text-[13px] font-semibold uppercase tracking-wider mb-3" style={{ color: TEXT_MUTED }}>
            Popular Picks
          </p>
          <div className="grid grid-cols-2 gap-3">
            {CURATED.map((c) => (
              <button
                key={c.label}
                onClick={() => openCategory(c.label)}
                className="flex items-center gap-3 h-14 px-4 rounded-2xl transition-transform active:scale-[0.98]"
                style={{ background: '#FFFFFF', border: '1px solid #F1F5F9' }}
              >
                <span
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: c.bg }}
                >
                  <c.icon className="w-[18px] h-[18px]" style={{ color: c.fg }} strokeWidth={2.2} />
                </span>
                <span className="text-[14px] font-semibold" style={{ color: TEXT_PRIMARY }}>
                  {c.label}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Everything else */}
        <section className="px-5 mt-7">
          <p className="text-[13px] font-semibold uppercase tracking-wider mb-3" style={{ color: TEXT_MUTED }}>
            All Categories
          </p>

          {loading && otherGenres.length === 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-[100px] rounded-2xl" style={{ background: SURFACE }} />
              ))}
            </div>
          ) : otherGenres.length === 0 ? (
            <p className="text-[14px] py-6 text-center" style={{ color: TEXT_MUTED }}>
              That's all for now. More categories as authors publish.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {otherGenres.map((g) => (
                <button
                  key={g.name}
                  onClick={() => openCategory(g.name)}
                  className="rounded-2xl p-4 text-left transition-transform active:scale-[0.98]"
                  style={{ background: BRAND_LIGHT }}
                >
                  <p className="text-[15px] font-bold mb-3 truncate" style={{ color: TEXT_PRIMARY }}>
                    {g.name}
                  </p>
                  <div className="flex gap-1 mb-2">
                    {g.coverSamples.slice(0, 3).map((c, i) => (
                      <div
                        key={i}
                        className="w-8 h-10 rounded-md overflow-hidden flex-shrink-0"
                        style={{ background: '#FFFFFF' }}
                      >
                        {c && <img src={c} alt="" className="w-full h-full object-cover" />}
                      </div>
                    ))}
                  </div>
                  <p className="text-[12px] font-medium" style={{ color: TEXT_MUTED }}>
                    {g.bookCount} book{g.bookCount === 1 ? '' : 's'}
                  </p>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
});

// Use BRAND const so the linter doesn't gripe about it being unused
// (kept for future style references).
void BRAND;

export default BrowseAllScreen;
